import { createPortal } from "react-dom";
import { Button } from "../components/ui/Button";
import EmptyState from "../components/EmptyState";
import LazyImage from "../components/ui/LazyImage";
import { Link, useParams } from "react-router-dom";
import NewsSection from "../components/NewsSection";
import { toPersianDate } from "../utils/dateFormatter";
import greenLogo from "../assets/images/green-logo.png";
import { isEmptyContent } from "../utils/isEmptyContent";
import ProjectGallery from "../components/ProjectGallery";
import { News, Project, ProjectNewsItem } from "../types";
import ManagerFollowBox from "../components/ManagerFollowBox";
import { useState, useEffect, useRef, useLayoutEffect } from "react";
import { useFollowUser } from "../features/managers/hooks/useFollowUser";
import { useFollowCheck } from "../features/managers/hooks/useFollowCheck";
import { useSingleProject } from "../features/home/hooks/useSingleProject";
import { useFollowProject } from "../features/managers/hooks/useFollowProject";
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from "../components/ui/Tabs";

const mapProjectNewsToNews = (
  project: Project,
  items?: ProjectNewsItem[],
): News[] => {
  if (!items?.length) return [];

  return items.map(
    (n): News => ({
      id: n.id,
      name: n.name,
      content: "",
      meta_text: null,
      subtitle: n.under_title ?? null,
      tags: [],

      images:
        Array.isArray(n.images) && n.images.length > 0
          ? n.images.map((url, index) => ({
              id: index,
              url,
              caption: null,
              type: "image",
            }))
          : n.image
            ? [
                {
                  id: 0,
                  url: n.image,
                  caption: null,
                  type: "image",
                },
              ]
            : [],

      projects: [
        {
          id: project.id,
          name: project.name,
          logo:
            typeof project.logo === "string"
              ? project.logo
              : (project.logo?.url ?? null),
        },
      ],

      categories: (project.categories ?? []).map((c: any) => ({
        id: c.id,
        name: c.name,
      })),

      is_featured: false,
      release_date: n.date_release ?? null,
      created_at: "",
      updated_at: "",
    }),
  );
};

export default function InstitutionDetail() {
  const { id } = useParams();
  const projectId = id ? Number(id) : undefined;
  const [activeTab, setActiveTab] = useState("introduction");
  const lastScrollY = useRef(0);
  const [showSticky, setShowSticky] = useState(false);
  const [isLogoViewerOpen, setIsLogoViewerOpen] = useState(false);
  const scrollUpDistance = useRef(0);
  const [expandedTab, setExpandedTab] = useState<
    "introduction" | "content" | null
  >(null);
  const expandTab = (tab: "introduction" | "content") => {
    setExpandedTab(tab);
  };
  const isIntroExpanded = expandedTab === "introduction";
  const isServicesExpanded = expandedTab === "content";
  const introRef = useRef<HTMLDivElement>(null);
  const servicesRef = useRef<HTMLDivElement>(null);
  const connectionsRef = useRef<HTMLDivElement>(null);
  const [isIntroOverflowing, setIsIntroOverflowing] = useState(false);
  const [isServicesOverflowing, setIsServicesOverflowing] = useState(false);
  const [isConnectionsOverflowing, setIsConnectionsOverflowing] =
    useState(false);
  const [projectFollowOverride, setProjectFollowOverride] = useState<
    boolean | null
  >(null);
  const [isProjectFollowLoading, setIsProjectFollowLoading] = useState(false);

  // ─── Data fetching ───────────────────────────────────────────
  const { data: project, isLoading } = useSingleProject(projectId);
  const isIntroductionEmpty = isEmptyContent(project?.introduction);
  const isConnectionsEmpty = isEmptyContent(project?.connections);
  const isServicesEmpty = isEmptyContent(project?.services);
  const personId = project?.persons?.[0]?.id;

  const { data: userFollowData, isFetching: userCheckLoading } = useFollowCheck(
    "persons",
    personId,
  );

  const { data: serverProjectFollowing, isFetching: projectCheckLoading } =
    useFollowCheck("projects", projectId);

  const { toggleFollowUser, loading: userFollowLoading } = useFollowUser();
  const { toggleFollowProject } = useFollowProject();

  // override > server state
  const currentFollowState =
    projectFollowOverride !== null
      ? projectFollowOverride
      : (serverProjectFollowing ?? false);

  const followButtonLoading = isProjectFollowLoading || projectCheckLoading;

  useLayoutEffect(() => {
    if (introRef.current) {
      const el = introRef.current;
      setIsIntroOverflowing(el.scrollHeight > el.clientHeight);
    }
  }, [project?.introduction, isIntroductionEmpty]);

  useLayoutEffect(() => {
    if (servicesRef.current) {
      const el = servicesRef.current;
      setIsServicesOverflowing(el.scrollHeight > el.clientHeight);
    }
  }, [project?.services, isServicesEmpty, activeTab]);

  useLayoutEffect(() => {
    if (connectionsRef.current) {
      const el = connectionsRef.current;
      setIsConnectionsOverflowing(el.scrollHeight > el.clientHeight);
    }
  }, [project?.connections, isConnectionsEmpty, activeTab]);
  // ─── Effects ─────────────────────────────────────────────────
  useEffect(() => {
    const handleScroll = () => {
      const isMobile = window.innerWidth < 1280;

      if (!isMobile) {
        setShowSticky(false);
        lastScrollY.current = 0;
        scrollUpDistance.current = 0;
        return;
      }

      const currentY = window.scrollY;
      const diff = currentY - lastScrollY.current;

      const goingDown = diff > 0;
      const goingUp = diff < 0;

      // ───────── SHOW ─────────
      if (goingDown && currentY > 120) {
        setShowSticky(true);
        scrollUpDistance.current = 0; // reset
      }

      // ───────── HIDE (FIX اصلی اینجاست) ─────────
      if (goingUp) {
        scrollUpDistance.current += Math.abs(diff);
        if (scrollUpDistance.current > 60) {
          setShowSticky(false);
        }
      } else {
        scrollUpDistance.current = 0;
      }

      lastScrollY.current = currentY;
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    window.addEventListener("resize", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleScroll);
    };
  }, []);

  // ─── Handlers ────────────────────────────────────────────────
  const handleToggleFollowUser = async () => {
    if (!personId) return;
    await toggleFollowUser(personId);
  };

  const handleToggleFollowProject = async () => {
    if (!project?.id) return;
    setIsProjectFollowLoading(true);
    const res = await toggleFollowProject(project.id);
    setIsProjectFollowLoading(false);
    if (res) setProjectFollowOverride(res.isFollowing);
  };

  // ─── Guards ──────────────────────────────────────────────────
  if (isLoading) {
    return (
      <div className="w-full min-h-screen bg-green-01">
        <div className="flex items-center justify-center w-full min-h-screen">
          <div className="size-8 border-2 border-green-03 border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="w-full min-h-screen bg-green-01">
        <div className="flex justify-center items-center h-screen">
          <p>اطلاعات پروژه یافت نشد</p>
        </div>
      </div>
    );
  }

  const manager = project.persons?.[0];
  const projectNews = mapProjectNewsToNews(project, project.news);
  const hasGallery = (project.images?.length ?? 0) > 0;
  const hasManager = project.persons?.length > 0;

  return (
    <div className="flex flex-col h-full justify-between min-h-screen bg-green-01">
      <div className="xl:flex xl:flex-row flex flex-col-reverse w-full items-start justify-center gap-x-4 px-4 pb-4 xl:pb-0 2xl:px-11 xl:my-5">
        {/* ── Aside ── */}
        <aside className="w-full xl:w-[25%] 2xl:w-[19%] space-y-2">
          {hasManager && (
            <ManagerFollowBox
              project={project}
              isFollowing={userFollowData ?? false}
              loading={userFollowLoading || userCheckLoading}
              onToggleFollow={handleToggleFollowUser}
              mode="desktop"
            />
          )}
          {hasGallery && (
            <div className="hidden xl:block">
              <ProjectGallery images={project.images} />
            </div>
          )}
          {/* <div className="!mt-7">
            <NewsSection
              form="form1"
              showMedia={false}
              newsData={projectNews}
            />
          </div> */}
        </aside>

        {/* ── Main ── */}
        <main className="w-full xl:w-[75%] 2xl:w-[81%]">
          {/* Desktop Header */}
          <div
            className={`hidden xl:flex items-center justify-between bg-white shadow-sm p-2.5 rounded-[20px]
            transition-all duration-500 ease-in-out
            transform origin-top overflow-hidden
            ${
              showSticky
                ? "opacity-0 -translate-y-6 scale-95 pointer-events-none"
                : "opacity-100 translate-y-0 scale-100"
            }`}
          >
            <div
              onClick={() => setIsLogoViewerOpen(true)}
              className="flex items-center justify-start h-full gap-x-2.5"
            >
              <LazyImage
                src={project.logo || greenLogo}
                alt={project.name}
                className="size-[122px] xl:size-[142px] shrink-0 object-cover bg-green-02 text-xs text-center border border-green-02 rounded-2xl"
              />
              <div className="w-full flex flex-col items-start justify-between pl-4 md:pl-0 gap-y-2">
                {project.categories.slice(0, 2).map((category) => (
                  <span
                    key={category.id}
                    className="text-xs xl:text-[15px] font-normal text-green-04"
                  >
                    {category.name}
                  </span>
                ))}
                <h2 className="text-base xl:text-xl text-green-05 font-bold">
                  {project.name}
                </h2>
                <h3 className="text-xs xl:text-sm font-medium text-green-04">
                  {project.date_establishment
                    ? toPersianDate(project.date_establishment)
                    : ""}
                </h3>
              </div>
            </div>
            <Button
              onClick={handleToggleFollowProject}
              disabled={followButtonLoading}
              className={`hidden xl:flex items-center justify-center gap-2 h-[40px] ml-12 transition ${
                currentFollowState
                  ? "bg-green-02 text-green-04 hover:bg-green-02/70 hover:text-green-04/70"
                  : ""
              }`}
            >
              {followButtonLoading ? (
                <span className="size-4 border-2 border-current border-t-transparent text-[15px] font-medium mx-5 rounded-full animate-spin" />
              ) : currentFollowState ? (
                "دنبال میکنید"
              ) : (
                "دنبال کردن"
              )}
            </Button>
          </div>

          {/* Mobile Header */}
          <div className="xl:hidden transition-all duration-300 ease-in-out">
            <div
              className={`bg-white xl:shadow p-3 overflow-hidden ${
                hasManager ? "rounded-t-[20px]" : "rounded-[20px]"
              }`}
            >
              <div
                onClick={() => setIsLogoViewerOpen(true)}
                className="flex items-center justify-start h-full gap-x-2.5"
              >
                <LazyImage
                  alt="project logo"
                  src={project.logo || greenLogo}
                  className="size-[122px] xl:size-[142px] shrink-0 object-cover bg-green-02 border border-green-02 rounded-2xl"
                />
                <div className="w-full flex flex-col items-start justify-between pl-3.5 space-y-1">
                  {project.categories.slice(0, 2).map((category) => (
                    <span
                      key={category.id}
                      className="text-xs font-medium text-green-06/55"
                    >
                      {category.name}
                    </span>
                  ))}
                  <h2 className="text-base text-green-05 font-bold">
                    {project.name}
                  </h2>
                  <h3 className="text-xs font-medium text-green-06/55">
                    {project.date_establishment
                      ? toPersianDate(project.date_establishment)
                      : ""}
                  </h3>
                  <Button
                    onClick={handleToggleFollowProject}
                    disabled={followButtonLoading}
                    className={`w-full md:w-fit xl:w-full flex items-center justify-center gap-2 text-xs font-normal transition rounded-[10px] ${
                      currentFollowState
                        ? "bg-green-02 hover:bg-green-02 text-green-04"
                        : ""
                    }`}
                  >
                    {followButtonLoading ? (
                      <span className="size-4 border-2 border-current border-t-transparent mx-5 rounded-full animate-spin" />
                    ) : currentFollowState ? (
                      "دنبال میکنید"
                    ) : (
                      "دنبال کردن"
                    )}
                  </Button>
                </div>
              </div>
            </div>
            {hasManager && (
              <ManagerFollowBox
                project={project}
                isFollowing={userFollowData ?? false}
                loading={userFollowLoading || userCheckLoading}
                onToggleFollow={handleToggleFollowUser}
                mode="mobile"
              />
            )}
          </div>

          {/* Sticky scroll bar */}
          <div
            className={`fixed top-0 left-0 right-0 z-30 transition-all duration-150 ease-in-out
            ${
              showSticky
                ? "opacity-100 translate-y-0"
                : "opacity-0 -translate-y-10 pointer-events-none"
            }`}
          >
            <div className="w-full bg-green-02/40 backdrop-blur-xl px-2.5 h-[72px] flex items-center justify-between pl-8 gap-3">
              <div className="flex items-center gap-2 min-w-0">
                <LazyImage
                  alt="project logo"
                  src={project.logo || greenLogo}
                  className="size-10 rounded-xl object-cover"
                />

                <h3 className="text-[9px] line-clamp-2 text-wrap font-bold text-green-05 truncate">
                  {project.name}
                </h3>
              </div>
              <Link
                to={`/managers/${manager?.id}`}
                className="flex items-center justify-start gap-2 xl:flex-row-reverse"
              >
                <LazyImage
                  alt="manager logo"
                  src={manager?.logo || greenLogo}
                  className="size-10 rounded-full object-cover"
                />
                <div className="text-right min-w-0 space-y-0.5">
                  <p className="text-[9px] text-green-04 truncate">
                    {manager?.nickname}
                  </p>
                  <h3 className="text-xs font-bold text-green-05 truncate">
                    {manager?.name}
                  </h3>
                </div>
              </Link>
            </div>
          </div>

          {/* Tabs */}
          <div className="w-full mt-2.5">
            <Tabs onValueChange={setActiveTab} defaultValue="introduction">
              <TabsList className="w-full !justify-center h-11 bg-white shadow-sm rounded-2xl">
                <div className="flex items-center justify-center gap-x-6">
                  <TabsTrigger
                    value="news"
                    className="!h-7 text-[13px] text-green-05 data-[state=active]:bg-green-01 data-[state=active]:text-green-05 font-medium !border-none px-2 shadow-none !rounded-lg"
                  >
                    اخبار
                  </TabsTrigger>
                  <TabsTrigger
                    value="connection"
                    className="!h-7 text-[13px] text-green-05 data-[state=active]:bg-green-01 data-[state=active]:text-green-05 font-medium !border-none px-2 shadow-none !rounded-lg"
                  >
                    راه های ارتباطی
                  </TabsTrigger>
                  <TabsTrigger
                    value="content"
                    className="!h-7 text-[13px] text-green-05 data-[state=active]:bg-green-01 data-[state=active]:text-green-05 font-medium !border-none px-2 shadow-none !rounded-lg"
                  >
                    خدمات
                  </TabsTrigger>
                  <TabsTrigger
                    value="introduction"
                    className="!h-7 text-[13px] text-green-05 data-[state=active]:bg-green-01 data-[state=active]:text-green-05 font-medium !border-none px-2 shadow-none !rounded-lg"
                  >
                    معرفی
                  </TabsTrigger>
                </div>
              </TabsList>

              <div
                className={`w-full
                  ${hasGallery ? "rounded-t-[20px]" : "rounded-[20px]"} 
                  md:rounded-[20px] 
                  overflow-hidden`}
              >
                <TabsContent
                  value="introduction"
                  dir="rtl"
                  className={`relative bg-white ${isIntroductionEmpty ? "pb-0" : "p-4 xl:p-9"} min-h-fit shadow-sm ${hasGallery ? "rounded-t-[20px]" : "mb-0.5 rounded-[20px]"} overflow-hidden xl:rounded-xl`}
                >
                  {isIntroductionEmpty ? (
                    <EmptyState />
                  ) : (
                    <div
                      ref={introRef}
                      className={`text-sm estedad3 leading-7 transition-all duration-300 ${
                        !isIntroExpanded ? "max-h-[13rem] overflow-hidden" : ""
                      }`}
                    >
                      <p
                        dangerouslySetInnerHTML={{
                          __html: project.introduction || "",
                        }}
                      />
                    </div>
                  )}
                  {!isIntroExpanded &&
                    !isIntroductionEmpty &&
                    isIntroOverflowing && (
                      <div className="absolute bottom-0 left-0 w-full h-28 bg-gradient-to-t from-white via-white/85 to-transparent" />
                    )}
                  {!isIntroExpanded &&
                    !isIntroductionEmpty &&
                    isIntroOverflowing && (
                      <div className="absolute bottom-4 left-0 w-full flex justify-center z-10">
                        <button
                          onClick={() => expandTab("introduction")}
                          className="text-green-04 text-[10px] px-3 h-7 bg-green-02 rounded-lg shadow-sm"
                        >
                          بیشتر
                        </button>
                      </div>
                    )}
                </TabsContent>
                <TabsContent
                  value="connection"
                  dir="rtl"
                  className={`relative bg-white ${
                    isConnectionsEmpty ? "pb-0" : "p-4 xl:p-9"
                  } min-h-fit shadow-sm ${
                    hasGallery ? "rounded-t-[20px]" : "mb-0.5 rounded-[20px]"
                  } overflow-hidden xl:rounded-xl`}
                >
                  {isConnectionsEmpty ? (
                    <EmptyState />
                  ) : (
                    <div
                      ref={connectionsRef}
                      className={`text-sm estedad3 leading-7 transition-all duration-300 ${
                        expandedTab !== "content"
                          ? "max-h-[13rem] overflow-hidden"
                          : ""
                      }`}
                    >
                      <p
                        dangerouslySetInnerHTML={{
                          __html: project.connections || "",
                        }}
                      />
                    </div>
                  )}

                  {!isConnectionsEmpty &&
                    expandedTab !== "content" &&
                    isConnectionsOverflowing && (
                      <div className="absolute bottom-0 left-0 w-full h-28 bg-gradient-to-t from-white via-white/85 to-transparent" />
                    )}

                  {!isConnectionsEmpty &&
                    expandedTab !== "content" &&
                    isConnectionsOverflowing && (
                      <div className="absolute bottom-4 left-0 w-full flex justify-center z-10">
                        <button
                          onClick={() => setExpandedTab("content")}
                          className="text-green-04 text-[10px] px-3 h-7 bg-green-02 rounded-lg shadow-sm"
                        >
                          بیشتر
                        </button>
                      </div>
                    )}
                </TabsContent>
                <TabsContent
                  value="news"
                  dir="rtl"
                  className="overflow-hidden rounded-[20px] xl:rounded-xl"
                >
                  <NewsSection
                    form="form1"
                    layout="grid"
                    showHeader={false}
                    showEmptyState
                    newsData={projectNews}
                  />
                </TabsContent>

                <TabsContent
                  value="content"
                  dir="rtl"
                  className={`relative bg-white ${isServicesEmpty ? "p-0" : "p-4 xl:p-9"} min-h-fit rounded-[20px] overflow-hidden xl:rounded-xl`}
                >
                  {isServicesEmpty ? (
                    <EmptyState />
                  ) : (
                    <div
                      ref={servicesRef}
                      className={`text-sm estedad3 leading-7 transition-all duration-300 ${
                        !isServicesExpanded
                          ? "max-h-[13rem] overflow-hidden"
                          : ""
                      }`}
                    >
                      <p
                        dangerouslySetInnerHTML={{
                          __html: project.services || "",
                        }}
                      />
                    </div>
                  )}
                  {!isServicesExpanded &&
                    !isServicesEmpty &&
                    isServicesOverflowing && (
                      <div className="absolute bottom-0 left-0 w-full h-28 bg-gradient-to-t from-white via-white/85 to-transparent" />
                    )}
                  {!isServicesExpanded &&
                    !isServicesEmpty &&
                    isServicesOverflowing && (
                      <div className="absolute bottom-4 left-0 w-full flex justify-center z-10">
                        <button
                          onClick={() => expandTab("content")}
                          className="text-green-04 text-[10px] px-3 h-7 bg-green-02 rounded-lg shadow-sm"
                        >
                          بیشتر
                        </button>
                      </div>
                    )}
                </TabsContent>
              </div>
            </Tabs>
          </div>
          {hasGallery && activeTab === "introduction" && (
            <div className="md:hidden xl:mt-4 bg-white shadow-sm rounded-b-[20px] xl:rounded-2xl overflow-hidden">
              <ProjectGallery images={project.images} />
            </div>
          )}
          {isLogoViewerOpen &&
            createPortal(
              <div
                className="fixed inset-0 z-[9999] bg-black/90 flex items-center justify-center p-4"
                onClick={() => setIsLogoViewerOpen(false)}
              >
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setIsLogoViewerOpen(false);
                  }}
                  className="absolute top-4 right-4 pt-1 size-10 rounded-full bg-white/10 text-white text-2xl flex items-center justify-center z-20"
                >
                  ×
                </button>

                <img
                  src={project.logo || greenLogo}
                  alt={project.name}
                  className="max-w-full max-h-[90vh] object-contain rounded-2xl select-none"
                  draggable={false}
                  onClick={(e) => e.stopPropagation()}
                />
              </div>,
              document.body,
            )}
        </main>
      </div>
    </div>
  );
}
